import React, { useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import AutoCompleteField from '@components/common/form/AutoCompleteField';
import CropImageField from '@components/common/form/CropImageField';
import { AppConstants, UserTypes } from '@constants';
import apiConfig from '@constants/apiConfig';
import { commonStatus, statusOptions } from '@constants/masterData';
import useBasicForm from '@hooks/useBasicForm';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { Card, Col, Form, Row } from 'antd';

const message = defineMessages({
    objectName: 'group permission',
});

const UserAdminForm = (props) => {
    const translate = useTranslate();
    const { formId, actions, dataDetail, onSubmit, setIsChangedFormValues, branchs, isEditing, roles } = props;
    const { execute: executeUpFile } = useFetch(apiConfig.file.upload);
    const [imageUrl, setImageUrl] = useState(null);
    const [groupOptions, setGroupOptions] = useState([]);
    const statusValues = translate.formatKeys(statusOptions, ['label']);


    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    const handleSubmit = (values) => {
        return mixinFuncs.handleSubmit(values);
    };

    useEffect(() => {
        if (!isEditing && groupOptions?.length > 0 && !form.getFieldValue('groupId')) {
            const roleOption = groupOptions.find((option) => option.label === 'ROLE INTERNAL');
            if (roleOption) {
                form.setFieldsValue({ groupId: roleOption.value });
            } else if (groupOptions.length > 0) {
                form.setFieldsValue({ groupId: groupOptions[0].value });
            }
        }
    }, [groupOptions, form, isEditing]);

    useEffect(() => {
        if (dataDetail) {
            const formValues = {
                ...dataDetail,
                fullName: dataDetail?.fullName,
                username: dataDetail?.username,
                email: dataDetail?.email,
                groupId: dataDetail?.group?.id,
            };

            if (isEditing) {
                delete formValues.password;
                delete formValues.confirmPassword;
            }
            form.setFieldsValue(formValues);
        }
    }, [dataDetail, form, isEditing]);

    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange}>
            <Card className="card-form" bordered={false}>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            disabled={isEditing}
                            label={translate.formatMessage(commonMessage.username)}
                            name="username"
                            autoComplete="off"
                            required
                        />
                    </Col>
                    <Col span={12}>
                        <TextField label={translate.formatMessage(commonMessage.fullName)} required name="fullName" />
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            label={translate.formatMessage(commonMessage.password)}
                            required={!isEditing}
                            name="password"
                            type="password"
                            autoComplete="new-password"
                            rules={[
                                {
                                    validator: async () => {
                                        const isTouched = form.isFieldTouched('password');
                                        if (isTouched) {
                                            const value = form.getFieldValue('password');
                                            if (value.length < 6) {
                                                throw new Error(
                                                    translate.formatMessage(commonMessage.validatePassword),
                                                );
                                            }
                                        }
                                    },
                                },
                            ]}
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            label={translate.formatMessage(commonMessage.confirmPassword)}
                            required={!isEditing}
                            name="confirmPassword"
                            type="password"
                            autoComplete="new-password"
                            rules={[
                                {
                                    validator: async () => {
                                        const password = form.getFieldValue('password');
                                        const confirmPassword = form.getFieldValue('confirmPassword');
                                        if (password !== confirmPassword) {
                                            throw new Error(translate.formatMessage(commonMessage.passwordNotMatch));
                                        }
                                    },
                                },
                            ]}
                        />
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <TextField label={translate.formatMessage(commonMessage.email)} name="email" type="email" autoComplete="off" rules={[{ type: 'email', message: translate.formatMessage(commonMessage.invalidEmail) }]} />
                    </Col>
                    <Col span={12}>
                        <AutoCompleteField
                            label={translate.formatMessage(commonMessage.groupPermission)}
                            name="groupId"
                            apiConfig={apiConfig.groupPermission.getList}
                            mappingOptions={(item) => ({ value: item.id, label: item.name })}
                            initialSearchParams={{ kind: UserTypes.ADMIN }}
                            onOptionsChange={(options) => setGroupOptions(options)} // Nhận options từ AutoCompleteField
                            disabled={dataDetail?.isSuperAdmin === true}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <SelectField
                            label={translate.formatMessage(commonMessage.status)}
                            name="status"
                            required
                            options={statusValues}
                            initialValue={statusValues[0].value}
                            disabled={dataDetail?.isSuperAdmin === true}
                        />
                    </Col>
                </Row>

                <div className="footer-card-form">{actions}</div>
            </Card>
        </BaseForm>
    );
};

export default UserAdminForm;
